
import { Link } from "react-router-dom";
import { LogOut, Menu, User, X, Shield } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose
} from "@/components/ui/sheet";
import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import NavigationMenu from "./header/NavigationMenu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "./ui/use-toast";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

const HomeHeader = () => {
  const { isAuthenticated, user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const checkUserRole = async () => {
      if (!isAuthenticated || !user) {
        setIsAdmin(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .rpc('get_user_role', {
            user_id: user.id
          });
          
        if (!error && (data === 'admin' || data === 'editor')) {
          setIsAdmin(true);
        }
      } catch (error) {
        console.error("Error checking user role:", error);
      }
    };
    
    checkUserRole();
  }, [isAuthenticated, user]);

  const handleLogout = async () => {
    if (isLoggingOut) return;

    try {
      setIsLoggingOut(true);
      
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      toast({
        title: "Logged out successfully",
        description: "You have been logged out of your account.",
      });

      navigate('/');
      
    } catch (error: any) {
      console.error("Logout error:", error);
      toast({
        title: "Error logging out",
        description: error.message || "An error occurred while logging out",
        variant: "destructive",
      });
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <header className="fixed top-4 right-4 z-50 flex items-center gap-2">
      {isAuthenticated && (
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/account">
                  <div className="flex items-center justify-center w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full hover:bg-white/20 transition-all duration-200">
                    <User className="h-5 w-5 text-white" />
                  </div>
                </Link>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="bg-white/10 backdrop-blur-sm text-white border-white/20">
                <p>My Account</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button 
                  onClick={handleLogout}
                  className="flex items-center justify-center w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full hover:bg-white/20 transition-all duration-200"
                  disabled={isLoggingOut}
                >
                  <LogOut className="h-5 w-5 text-white" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="bg-white/10 backdrop-blur-sm text-white border-white/20">
                <p>Logout</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          {isAdmin && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link to="/admin" className="flex items-center justify-center w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full hover:bg-white/20 transition-all duration-200">
                    <Shield className="h-5 w-5 text-white" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-white/10 backdrop-blur-sm text-white border-white/20">
                  <p>Admin Dashboard</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}
      <Sheet>
        <SheetTrigger asChild>
          <button className="flex items-center justify-center w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full hover:bg-white/20 transition-all duration-200">
            <Menu className="h-5 w-5 text-white" />
          </button>
        </SheetTrigger>
        <SheetContent side="right" className="w-[300px] sm:w-[350px] p-0 bg-gradient-to-b from-corporate-primary-100 to-corporate-primary-75 border-l border-white/10 shadow-xl">
          <NavigationMenu />
          <SheetClose className="absolute right-4 top-4 flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 transition-all duration-200">
            <X className="h-4 w-4 text-white" />
          </SheetClose>
        </SheetContent>
      </Sheet>
    </header>
  );
};

export default HomeHeader;
